<template>
  <div class="ea">

    <!-- Hero -->
    <header class="ea__hero">
      <div class="ea__hero-overlay"></div>
      <div class="ea__hero-inner">
        <div class="ea__hero-icon">
          <i class="fas fa-chart-line"></i>
        </div>
        <h1 class="ea__hero-title">
          <span>{{ $t('blog.safeInvestment') }}</span>
          <em>{{ $t('common.profit') }}</em>
        </h1>
        <p class="ea__hero-sub">{{ $t('blog.blog2.withEA') }}</p>
        <button class="ea__hero-btn" @click="goToContact">
          <i class="fas fa-rocket"></i>
          {{ $t('common.start') }}
        </button>
      </div>
    </header>

    <!-- Problem -->
    <section class="ea__section ea__section--white">
      <div class="ea__shell">
        <div class="ea__section-header">
          <h2 class="ea__section-title">{{ $t('blog.blog2.timeInvest') }}</h2>
          <div class="ea__divider"></div>
        </div>
        <div class="ea__problem-card">
          <div class="ea__problem-text">
            <h3 class="ea__card-title">{{ $t('blog.blog2.weUnderstand') }}</h3>
            <ul class="ea__problem-list">
              <li><i class="fas fa-clock"></i>{{ $t('blog.blog2.notEnoughTime') }}</li>
              <li><i class="fas fa-chart-bar"></i>{{ $t('blog.blog2.notEnoughKnowledge') }}</li>
              <li><i class="fas fa-exclamation-triangle"></i>{{ $t('blog.blog2.worry') }}</li>
            </ul>
          </div>
          <div class="ea__problem-icon">
            <i class="fas fa-user-clock"></i>
          </div>
        </div>
      </div>
    </section>

    <!-- Features -->
    <section class="ea__section ea__section--cream">
      <div class="ea__shell">
        <div class="ea__section-header">
          <h2 class="ea__section-title">{{ $t('blog.blog2.solution') }}</h2>
          <p class="ea__section-sub">{{ $t('blog.blog2.EASystem') }}</p>
        </div>
        <div class="ea__feature-grid">
          <div class="ea__feature-card">
            <div class="ea__feature-icon"><i class="fas fa-robot"></i></div>
            <h3>{{ $t('blog.EAAuto') }}</h3>
            <p>{{ $t('blog.blog2.EAAuto') }}</p>
          </div>
          <div class="ea__feature-card">
            <div class="ea__feature-icon"><i class="fas fa-percentage"></i></div>
            <h3>{{ $t('blog.blog2.profit10') }}</h3>
            <p>{{ $t('blog.blog2.profitStable') }}</p>
          </div>
          <div class="ea__feature-card">
            <div class="ea__feature-icon"><i class="fas fa-shield-alt"></i></div>
            <h3>{{ $t('blog.blog2.lowRisk') }}</h3>
            <p>{{ $t('blog.blog2.risk23') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats -->
    <section class="ea__stats">
      <div class="ea__shell">
        <div class="ea__stats-grid">
          <div class="ea__stat">
            <strong>10%</strong>
            <span>{{ $t('blog.blog2.profitMonth') }}</span>
          </div>
          <div class="ea__stat">
            <strong>12</strong>
            <span>{{ $t('blog.blog2.EA') }}</span>
          </div>
          <div class="ea__stat">
            <strong>20–30%</strong>
            <span>{{ $t('blog.blog2.risk') }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Target audience -->
    <section class="ea__section ea__section--white">
      <div class="ea__shell">
        <div class="ea__section-header">
          <h2 class="ea__section-title">{{ $t('blog.blog2.who') }}</h2>
        </div>
        <div class="ea__audience-grid">
          <div class="ea__audience-card">
            <i class="fas fa-user-tie"></i>
            <h3>{{ $t('blog.blog2.person') }}</h3>
            <p>{{ $t('blog.blog2.personInvest') }}</p>
          </div>
          <div class="ea__audience-card">
            <i class="fas fa-briefcase"></i>
            <h3>{{ $t('blog.blog2.busy') }}</h3>
            <p>{{ $t('blog.blog2.missTime') }}</p>
          </div>
          <div class="ea__audience-card">
            <i class="fas fa-graduation-cap"></i>
            <h3>{{ $t('blog.blog2.newBie') }}</h3>
            <p>{{ $t('blog.blog2.missEx') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Transparency -->
    <section class="ea__section ea__section--cream">
      <div class="ea__shell">
        <div class="ea__transparency-card">
          <div class="ea__transparency-header">
            <i class="fas fa-chart-line"></i>
            <h2>{{ $t('blog.blog2.efficiency') }}</h2>
            <p>{{ $t('blog.blog2.followLive') }}</p>
          </div>
          <ul class="ea__transparency-list">
            <li><i class="fas fa-eye"></i>{{ $t('blog.blog2.followRealtime') }}</li>
            <li><i class="fas fa-chart-bar"></i>{{ $t('blog.blog2.report') }}</li>
            <li><i class="fas fa-shield-alt"></i>{{ $t('blog.blog2.dataValid') }}</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- CTA / Open account -->
    <section class="ea__cta-section">
      <div class="ea__shell">
        <h2 class="ea__cta-title">{{ $t('blog.blog2.startSmart') }}</h2>
        <div class="ea__cta-cards">
          <div class="ea__cta-card">
            <h3>{{ $t('blog.blog2.openCT') }}</h3>
            <a href="https://vigco.co/AzeuYj" target="_blank" rel="noopener noreferrer" class="ea__cta-link">
              https://vigco.co/AzeuYj
            </a>
          </div>
          <div class="ea__cta-card">
            <h3>{{ $t('blog.blog2.code') }}</h3>
            <code class="ea__cta-code">SHCcapital</code>
          </div>
        </div>
      </div>
    </section>

    <!-- Partners -->
    <section class="ea__section ea__section--white">
      <div class="ea__shell">
        <div class="ea__section-header">
          <h2 class="ea__section-title">{{ $t('blog.blog2.partner') }}</h2>
          <p class="ea__section-sub">{{ $t('blog.blog2.partnerWithUs') }}</p>
        </div>
        <div class="ea__partner-grid">
          <div
            v-for="(image, index) in partnerImages"
            :key="index"
            class="ea__partner-card"
            @click="openImageModal(image, '')"
          >
            <img :src="image" :alt="`Đối tác ${index + 1}`" class="ea__partner-img" />
          </div>
        </div>
      </div>
    </section>

    <!-- Contact -->
    <section class="ea__section ea__section--white">
      <div class="ea__shell">
        <div class="ea__section-header">
          <h2 class="ea__section-title">{{ $t('common.advise') }}</h2>
          <p class="ea__section-sub">{{ $t('blog.blog2.advise') }}</p>
        </div>
        <div class="ea__contact-grid">
          <div class="ea__contact-card ea__contact-card--tg">
            <i class="fab fa-telegram"></i>
            <h3>{{ $t('common.telegram') }}</h3>
            <p>@SHC Rim</p>
          </div>
          <div class="ea__contact-card ea__contact-card--phone">
            <i class="fas fa-phone"></i>
            <h3>{{ $t('common.zalo') }}</h3>
            <p>+84.766.726.416</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Image modal -->
    <div v-if="showImageModal" class="ea__modal" @click="closeImageModal">
      <div class="ea__modal-box" @click.stop>
        <button class="ea__modal-close" @click="closeImageModal">
          <i class="fas fa-times"></i>
        </button>
        <img :src="selectedImage.src" :alt="selectedImage.alt" class="ea__modal-img" />
        <div class="ea__modal-caption">
          <h4>{{ selectedImage.alt }}</h4>
          <p>{{ $t('blog.blog2.close') }}</p>
        </div>
      </div>
    </div>

  </div>
</template>

<script src="./ea-shc.js"></script>
<style src="./ea-shc.scss" lang="scss"></style>
